import React from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from '../utils/useReveal';

/**
 * Gold Star Gardens memorial band — reverent treatment: deep green field,
 * generous whitespace, a thin gold rule, and a single slow fade-in
 * (`oli-reveal-memorial`). Deliberately no slide/stagger/parallax motion.
 *
 * Props:
 *   title    — heading (default below)
 *   children — body copy override
 *   to / cta — destination + label for the single CTA
 */
export const GoldStarSection = ({
  title = 'Gold Star Gardens',
  children,
  to = '/gold-star-families',
  cta = 'Visit the Gold Star Gardens',
}) => {
  const [ref, revealed] = useReveal({ threshold: 0.2 });

  return (
    <section className="bg-forest_deep" aria-labelledby="gold-star-heading">
      <div
        ref={ref}
        className={`oli-reveal-memorial ${revealed ? 'is-revealed' : ''} max-w-3xl mx-auto px-6 sm:px-8 py-20 sm:py-28 text-center`}
      >
        {/* Gold star emblem */}
        <svg
          className="w-10 h-10 mx-auto text-gold_soft"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 2l2.6 6.9 7.4.4-5.8 4.7 1.9 7.2L12 17.3 5.9 21.2l1.9-7.2L2 9.3l7.4-.4L12 2z" />
        </svg>

        <div className="w-16 h-px bg-gold/60 mx-auto mt-8 mb-8" aria-hidden="true" />

        <h2 id="gold-star-heading" className="font-display font-semibold text-3xl sm:text-4xl text-cream leading-snug">
          {title}
        </h2>

        <div className="text-cream/75 text-base sm:text-lg leading-loose mt-6 max-w-2xl mx-auto">
          {children || (
            <p>
              Every Gold Star Garden is planted in the name of a fallen service member or first
              responder — a living tribute that feeds the community they gave everything to
              protect. We tend these gardens with their families, so that remembrance grows
              something lasting.
            </p>
          )}
        </div>

        {to && cta && (
          <Link
            to={to}
            className="inline-flex items-center justify-center mt-10 px-8 py-3.5 rounded-btn border border-gold/70 text-gold_soft hover:bg-gold hover:text-forest_deep font-bold text-sm uppercase tracking-label transition-colors duration-300"
          >
            {cta}
          </Link>
        )}
      </div>
    </section>
  );
};

export default GoldStarSection;
